/**
 * Natural (numeric-aware) string comparison + a title heuristic, used by the
 * Filevine grouped pull to order scan fragments the way the process that
 * fragmented them intended (`SCAN_2` before `SCAN_10`, `Vol 1 of 4` before
 * `Vol 2 of 4`).
 */

/**
 * Compare two names case-insensitively, treating runs of digits as numbers
 * (leading zeros ignored, arbitrary length — no parseInt precision loss).
 * Returns <0, 0, >0 like a standard comparator.
 */
export const naturalCompare = (a: string, b: string): number => {
    const tokenize = (s: string): string[] => s.toLowerCase().match(/(\d+)|(\D+)/g) ?? [];
    const at = tokenize(a);
    const bt = tokenize(b);
    const len = Math.min(at.length, bt.length);
    for (let i = 0; i < len; i++) {
        const as = at[i];
        const bs = bt[i];
        const aNum = /^\d/.test(as);
        const bNum = /^\d/.test(bs);
        if (aNum && bNum) {
            const aTrim = as.replace(/^0+/, '') || '0';
            const bTrim = bs.replace(/^0+/, '') || '0';
            if (aTrim.length !== bTrim.length) return aTrim.length < bTrim.length ? -1 : 1;
            if (aTrim !== bTrim) return aTrim < bTrim ? -1 : 1;
        } else if (as !== bs) {
            return as < bs ? -1 : 1;
        }
    }
    if (at.length !== bt.length) return at.length < bt.length ? -1 : 1;
    return 0;
};

/**
 * Derive a default title for a combined document from its fragment filenames:
 * the longest common prefix of the stems, with the trailing fragment counter
 * (partial digits, separators, and `vol|volume|part|pt|of|no` tokens)
 * stripped. Falls back to the first stem when the residue is too short.
 */
export const commonStemTitle = (names: string[]): string | null => {
    if (names.length === 0) return null;
    const stems = names.map(n => n.replace(/\.[^.]+$/, ''));
    let prefix = stems[0];
    for (const stem of stems.slice(1)) {
        let i = 0;
        const max = Math.min(prefix.length, stem.length);
        while (i < max && prefix[i].toLowerCase() === stem[i].toLowerCase()) i++;
        prefix = prefix.slice(0, i);
        if (!prefix) break;
    }
    // Strip, from the end: a partial digit run (the divergent counter), then
    // separators, then any fragment tokens (with their own digits/separators).
    let cleaned = prefix.replace(/\d+$/, '').replace(/[\s_\-.,()#]+$/, '');
    while (true) {
        // A fragment token counts only as a WHOLE word (separator before it),
        // so "Transcript" never loses its trailing "pt".
        const next = cleaned.replace(/[\s_\-.,()#]+(?:vol(?:ume)?|part|pt|of|no)[\s_\-.,()#]*\d*$/i, '');
        if (next === cleaned) break;
        cleaned = next.replace(/[\s_\-.,()#]+$/, '');
    }
    cleaned = cleaned.trim();
    if (cleaned.length >= 3) return cleaned;
    const fallback = stems[0].trim();
    return fallback || null;
};
